import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import PostSignup from './pages/PostSignup';
import Payment from './pages/Payment';
import CreatePassword from './pages/CreatePassword';
import Welcome from './pages/Welcome';
import Dashboard from './pages/Dashboard';
import LoginPage from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import Billing from './pages/Billing';
import AdminUsers from './pages/AdminUsers';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';
import SubscriptionSuccess from './components/subscription/SubscriptionSuccess';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import CCPA from './pages/CCPA';
import UserProvider from './context/UserContext';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import './App.css';

// Component to conditionally render Navbar
const AppContent: React.FC = () => {
  const location = useLocation();
  
  // Pages where Navbar should NOT be shown
  const hideNavbarPaths = ['/', '/post-signup', '/payment', '/payment/success', '/payment/cancel', '/payment-cancelled', '/create-password', '/welcome', '/login', '/forgot-password'];
  const isInstallPage = location.pathname === '/install' || location.pathname.startsWith('/install/');
  // Also hide navbar for reset-password routes (with or without token)
  const isResetPasswordPage = location.pathname === '/reset-password' || location.pathname.startsWith('/reset-password/');
  const shouldShowNavbar = !hideNavbarPaths.includes(location.pathname) && !isResetPasswordPage && !isInstallPage;

  return (
    <div className="app">
      {shouldShowNavbar && <Navbar />}
      <main className={shouldShowNavbar ? "main-content" : ""}>
        <Routes>
          {/* Public landing pages - no authentication required */}
          <Route path="/" element={<Home />} />
          <Route path="/payment" element={<Payment />} />
          <Route path="/create-password" element={<CreatePassword />} />
          <Route path="/post-signup" element={<PostSignup />} />
          <Route path="/install" element={<PostSignup />} />
          <Route path="/install/:browser" element={<PostSignup />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/ccpa" element={<CCPA />} />
          
          {/* Auth pages - no authentication required */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:resettoken" element={<ResetPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Protected dashboard pages - authentication required */}
          <Route 
            path="/welcome" 
            element={
              <PrivateRoute>
                <Welcome />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/billing" 
            element={
              <PrivateRoute>
                <Billing />
              </PrivateRoute>
            } 
          />
          <Route
            path="/admin/users"
            element={
              <PrivateRoute>
                <AdminUsers />
              </PrivateRoute>
            }
          />
          <Route 
            path="/subscription-success" 
            element={
              <PrivateRoute>
                <SubscriptionSuccess />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/payment/success" 
            element={<PaymentSuccess />} 
          />
          <Route 
            path="/payment/cancel" 
            element={<PaymentCancelled />} 
          />
          <Route 
            path="/payment-cancelled" 
            element={<PaymentCancelled />} 
          />
        </Routes>
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <UserProvider>
        <Router>
          <AppContent />
        </Router>
      </UserProvider>
    </AuthProvider>
  );
};

export default App;
